/**
 * Subscription Service — 결제 재작업 Phase 3
 *
 * Supabase에서 학원(academy) 좌석권 구독 + 좌석 수를 조회해 스토어에 반영.
 * 개인(personal) 구독은 폐기 — academy 만.
 *
 * @module services/subscriptionService
 */

import { supabase, isSupabaseConfigured } from "~/shared/lib/supabase";
import { queryWithAuthRetry } from "~/core/api/executeWithAuthRetry";
import {
  useSubscriptionStore,
  type BillingCycle,
  type Subscription,
  type SubscriptionPlan,
  type SubscriptionStatus,
} from "~/core/store/useSubscriptionStore";

// ============================================================================
// TYPES
// ============================================================================

interface SubscriptionRow {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  billing_cycle: BillingCycle | null;
  seat_count: number | null;
  price_per_seat: number | null;
  trial_ends_at: string | null;
  grace_period_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  next_billing_cycle: BillingCycle | null;
}

const SUB_SELECT =
  "id, plan, status, billing_cycle, seat_count, price_per_seat, trial_ends_at, grace_period_ends_at, current_period_end, cancel_at_period_end, next_billing_cycle" as const;

function rowToSubscription(row: SubscriptionRow, pausedSeatCount: number): Subscription {
  return {
    id: row.id,
    plan: row.plan,
    status: row.status,
    billingCycle: row.billing_cycle ?? "monthly",
    activeSeatCount: row.seat_count ?? 0,
    pausedSeatCount,
    pricePerSeat: row.price_per_seat,
    trialEndsAt: row.trial_ends_at,
    gracePeriodEndsAt: row.grace_period_ends_at,
    currentPeriodEnd: row.current_period_end,
    cancelAtPeriodEnd: row.cancel_at_period_end ?? false,
    nextBillingCycle: row.next_billing_cycle ?? null,
  };
}

// ============================================================================
// QUERIES
// ============================================================================

/** 멈춤 좌석 수 조회 (OWNER/ADMIN 만 정확 — RLS. 그 외/실패 시 0). 표시용 best-effort. */
async function fetchPausedSeatCount(academyId: string): Promise<number> {
  if (!isSupabaseConfigured() || !supabase) return 0;
  const { count, error } = await supabase
    .from("seat_assignments")
    .select("id", { count: "exact", head: true })
    .eq("academy_id", academyId)
    .eq("status", "paused");
  if (error) return 0;
  return count ?? 0;
}

/**
 * 학원 구독 조회 (구독 행 + 멈춤 좌석 수). active 좌석 수는 seat_count 캐시.
 *
 * 🔴 ISSUE_195 D4: **조회 실패(null 반환)와 "구독 없음"을 섞지 말 것.**
 * 예전엔 error 도 null 로 뭉개서, 네트워크/RLS 실패가 "무료 사용자"와 구분되지 않았다.
 * 이제 실패는 throw 한다 — 호출부가 재시도하고 loadState 를 'error' 로 남길 수 있도록.
 */
export async function fetchAcademySubscription(academyId: string): Promise<Subscription | null> {
  if (!isSupabaseConfigured() || !supabase) return null;
  const client = supabase;

  const { data, error } = await queryWithAuthRetry<SubscriptionRow | null>(() =>
    client
      .from("subscriptions")
      .select(SUB_SELECT)
      .eq("academy_id", academyId)
      .maybeSingle()
  );

  if (error) {
    throw new Error(`구독 조회 실패: ${error.message ?? String(error)}`);
  }
  if (!data) return null; // 조회는 성공했고, 구독 행이 없다 = 확실한 무료

  const pausedSeatCount = await fetchPausedSeatCount(academyId);
  return rowToSubscription(data, pausedSeatCount);
}

/** 구독 조회 재시도 간격 (ISSUE_195 D4) — 일시적 네트워크 실패로 무료 강등되지 않도록 */
const SUB_RETRY_DELAYS_MS = [1_000, 3_000, 8_000];

/**
 * 구독 정보 로드 → 스토어 반영. academyId 없으면(학원 미선택) 구독 없음.
 *
 * 🔴 ISSUE_195 D4: 실패를 조용히 삼키면 `academySubscription` 이 null 로 남아
 * **결제 사용자도 무료로 강등**된다(세션 동기화 OFF). 백오프 재시도하고,
 * 끝내 실패하면 loadState='error' 로 남겨 "모름"을 드러낸다.
 */
export async function loadSubscriptions(academyId: string | null): Promise<void> {
  const store = useSubscriptionStore.getState();

  if (!academyId) {
    store.setAcademySubscription(null); // 학원 미선택 = 확실한 무료
    return;
  }

  store.setLoading(true);
  store.setLoadState("loading");

  try {
    for (let attempt = 0; ; attempt++) {
      try {
        store.setAcademySubscription(await fetchAcademySubscription(academyId));
        return;
      } catch (err) {
        const delay = SUB_RETRY_DELAYS_MS[attempt];
        if (delay === undefined) {
          console.error("[subscriptionService] 구독 조회 최종 실패 — 동기화가 잠긴 상태로 남는다", err);
          store.setLoadState("error");
          return;
        }
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  } finally {
    store.setLoading(false);
  }
}
